import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

export const DIALOG_TYPES = {
  EXIT_CONFIRM: 'exitConfirm',
  PATIENT_CONSENT: 'patientConsent',
  SESSION_DISCARD: 'sessionDiscard',
  TELEHEALTH_INSTRUCTIONS: 'telehealthInstructions',
} as const;

export const EXIT_ACTIONS = {
  LOGOUT: 'logout',
  CLOSE: 'close',
} as const;

export const CONSENT_ACTIONS = {
  CONSENT: 'consent',
  CANCEL: 'cancel',
} as const;

export const SESSION_DISCARD_ACTIONS = {
  DISCARD: 'discard',
  CONTINUE: 'continue',
  CANCEL: 'cancel',
} as const;

export const TELEHEALTH_INSTRUCTION_ACTIONS = {
  CONTINUE: 'continue',
  CANCEL: 'cancel',
} as const;

type CoreDialogType = (typeof DIALOG_TYPES)[keyof typeof DIALOG_TYPES];
type DialogType = CoreDialogType | (string & {});

export type ExitActionType = (typeof EXIT_ACTIONS)[keyof typeof EXIT_ACTIONS];
export type ConsentActionType =
  (typeof CONSENT_ACTIONS)[keyof typeof CONSENT_ACTIONS];
export type SessionDiscardActionType =
  (typeof SESSION_DISCARD_ACTIONS)[keyof typeof SESSION_DISCARD_ACTIONS];
export type TelehealthInstructionActionType =
  (typeof TELEHEALTH_INSTRUCTION_ACTIONS)[keyof typeof TELEHEALTH_INSTRUCTION_ACTIONS];

export interface ExitConfirmData {
  action: ExitActionType;
}

export interface PatientConsentData {
  patientName: string;
}

export interface SessionDiscardData {
  hasTranscript: boolean;
  hasDocument: boolean;
  canContinue?: boolean;
}

export type TelehealthInstructionsData = Record<string, never>;

type DialogTypeMap = {
  [DIALOG_TYPES.EXIT_CONFIRM]: ExitConfirmData;
  [DIALOG_TYPES.PATIENT_CONSENT]: PatientConsentData;
  [DIALOG_TYPES.SESSION_DISCARD]: SessionDiscardData;
  [DIALOG_TYPES.TELEHEALTH_INSTRUCTIONS]: TelehealthInstructionsData;
};

interface DialogConfig<TDialogType extends DialogType = DialogType> {
  type: TDialogType;
  data?: TDialogType extends CoreDialogType
    ? DialogTypeMap[TDialogType]
    : Record<string, unknown>;
  timestamp: number;
  resolve?: (value: unknown) => void;
  reject?: (reason?: unknown) => void;
}

interface DialogState {
  activeDialogs: Map<DialogType, DialogConfig>;

  openDialog: <T = unknown>(
    type: DialogType,
    data?: Record<string, unknown> | ExitConfirmData
  ) => Promise<T>;

  closeDialog: (type: DialogType, result?: unknown) => void;

  getDialogData: <T = Record<string, unknown>>(
    type: DialogType
  ) => T | undefined;

  resetStore: () => void;
}

const initialDialogState = {
  activeDialogs: new Map(),
};

export const useDialogStore = create<DialogState>()(
  devtools(
    (set, get) => ({
      ...initialDialogState,

      openDialog: <T = unknown>(
        type: DialogType,
        data?: Record<string, unknown> | ExitConfirmData
      ): Promise<T> => {
        const promise = new Promise<T>((resolve, reject) => {
          const dialogConfig: DialogConfig = {
            type,
            data,
            timestamp: Date.now(),
            resolve: resolve as (value: unknown) => void,
            reject: reject as (reason?: unknown) => void,
          };

          set(
            (state) => ({
              activeDialogs: new Map(state.activeDialogs).set(
                type,
                dialogConfig
              ),
            }),
            false,
            `dialog/openDialog/${type}`
          );
        });
        promise.catch(() => undefined);
        return promise;
      },

      closeDialog: (type: DialogType, result?: unknown) => {
        const { activeDialogs } = get();
        const dialog = activeDialogs.get(type);

        if (result !== undefined) {
          dialog?.resolve?.(result);
        }

        set(
          (state) => {
            const updatedDialogs = new Map(state.activeDialogs);
            updatedDialogs.delete(type);
            return { activeDialogs: updatedDialogs };
          },
          false,
          `dialog/closeDialog/${type}`
        );
      },

      getDialogData: <T = Record<string, unknown>>(type: DialogType) => {
        const { activeDialogs } = get();
        const dialog = activeDialogs.get(type);
        return dialog?.data as T | undefined;
      },

      resetStore: () => {
        const { activeDialogs } = get();
        for (const dialog of activeDialogs.values()) {
          dialog.reject?.(new Error('Dialog store was reset'));
        }
        set({ activeDialogs: new Map() }, false, 'dialog/resetStore');
      },
    }),
    { name: 'dialog-store' }
  )
);
